import React, { useMemo, useState } from "react";
import Dropdown from "../../../common/dropDown";
import { findOption } from "../epsfLedgerUi";
import {
  DOSAGE_FORM_OPTIONS,
  ROUTE_OPTIONS,
  STRENGTH_UNIT_OPTIONS,
  TEMP_UNIT_OPTIONS,
  mergeLookupOptions,
} from "./impProductOptions";

const OTHER_VALUE = "Other";

const TEXT_FIELDS = [
  { key: "productCode", label: "Code", placeholder: "IMP-001", minWidth: "6.5rem" },
  { key: "productName", label: "Name", placeholder: "Study Drug A", minWidth: "11rem" },
  { key: "dosageForm", label: "Dosage form", type: "dropdown", minWidth: "9rem" },
  { key: "route", label: "Route", type: "dropdown", minWidth: "8.5rem" },
  { key: "strengthValue", label: "Strength", placeholder: "50", minWidth: "4.75rem" },
  { key: "strengthUnit", label: "Unit", type: "dropdown", minWidth: "6.5rem" },
  {
    key: "manufacturer",
    label: "Manufacturer",
    placeholder: "ABC Pharma",
    minWidth: "8rem",
  },
  { key: "storageTempUnit", label: "°C/°F", type: "temp", minWidth: "5.25rem" },
  { key: "storageTempMin", label: "Min", placeholder: "2", minWidth: "4rem" },
  { key: "storageTempMax", label: "Max", placeholder: "8", minWidth: "4rem" },
  {
    key: "storageInstructions",
    label: "Storage instructions",
    placeholder: "Protect from light",
    minWidth: "11rem",
  },
];

const OTHER_META = {
  dosageForm: {
    title: "Add dosage form",
    label: "Dosage form",
    placeholder: "e.g. Softgel Capsule",
    maxLength: 80,
  },
  route: {
    title: "Add route of administration",
    label: "Route of administration",
    placeholder: "e.g. Epidural",
    maxLength: 80,
  },
  strengthUnit: {
    title: "Add strength unit",
    label: "Strength unit",
    placeholder: "e.g. mg/g",
    maxLength: 40,
  },
};

function collectExtras(drafts, key) {
  const seen = new Set();
  const extras = [];
  (drafts || []).forEach((d) => {
    const v = String(d?.[key] || "").trim();
    if (!v || v.toLowerCase() === "other") return;
    const k = v.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    extras.push({ value: v, label: v });
  });
  return extras;
}

function findExistingOption(options, typed) {
  const needle = String(typed || "")
    .trim()
    .toLowerCase();
  if (!needle || needle === "other") return null;
  return (
    (options || []).find((opt) => {
      const v = String(opt?.value ?? "")
        .trim()
        .toLowerCase();
      const l = String(opt?.label ?? "")
        .trim()
        .toLowerCase();
      if (!v || v === "other") return false;
      return v === needle || l === needle;
    }) || null
  );
}

export default function ImpProductImportSheet({
  drafts = [],
  errorsById = {},
  parseErrors = [],
  statusMessage = "",
  onUpdateDraft,
  onRemoveDraft,
  onAddRow,
}) {
  const [otherModal, setOtherModal] = useState(null);
  const showFailBanner =
    Boolean(statusMessage) && drafts.length === 0 && !parseErrors.length;

  const sortedDrafts = useMemo(() => {
    const list = Array.isArray(drafts) ? [...drafts] : [];
    list.sort((a, b) => {
      const aBad = errorsById[a?.id] ? 1 : 0;
      const bBad = errorsById[b?.id] ? 1 : 0;
      if (aBad !== bBad) return bBad - aBad;
      return 0;
    });
    return list;
  }, [drafts, errorsById]);

  const dosageOptions = useMemo(
    () =>
      mergeLookupOptions(
        DOSAGE_FORM_OPTIONS,
        collectExtras(drafts, "dosageForm"),
      ),
    [drafts],
  );
  const routeOptions = useMemo(
    () => mergeLookupOptions(ROUTE_OPTIONS, collectExtras(drafts, "route")),
    [drafts],
  );
  const strengthUnitOptions = useMemo(
    () =>
      mergeLookupOptions(
        STRENGTH_UNIT_OPTIONS,
        collectExtras(drafts, "strengthUnit"),
      ),
    [drafts],
  );

  const optionsForField = (key) => {
    if (key === "dosageForm") return dosageOptions;
    if (key === "route") return routeOptions;
    if (key === "strengthUnit") return strengthUnitOptions;
    if (key === "storageTempUnit") return TEMP_UNIT_OPTIONS;
    return [];
  };

  const openOther = (draftId, field) => {
    const meta = OTHER_META[field];
    if (!meta) return;
    setOtherModal({
      draftId,
      field,
      draft: "",
      error: "",
      ...meta,
    });
  };

  const closeOther = () => setOtherModal(null);

  const confirmOther = () => {
    if (!otherModal) return;
    const typed = String(otherModal.draft || "").trim();
    const max = otherModal.maxLength || 80;
    if (!typed) {
      setOtherModal((prev) =>
        prev ? { ...prev, error: `${prev.label} is required.` } : prev,
      );
      return;
    }
    if (typed.toLowerCase() === "other") {
      setOtherModal((prev) =>
        prev
          ? { ...prev, error: "Please enter a specific value, not Other." }
          : prev,
      );
      return;
    }
    if (typed.length > max) {
      setOtherModal((prev) =>
        prev
          ? {
              ...prev,
              error: `${prev.label} cannot exceed ${max} characters.`,
            }
          : prev,
      );
      return;
    }
    const opts = optionsForField(otherModal.field);
    const existing = findExistingOption(opts, typed);
    const finalValue = existing ? String(existing.value).trim() : typed;
    onUpdateDraft(otherModal.draftId, (d) => ({
      ...d,
      [otherModal.field]: finalValue,
    }));
    setOtherModal(null);
  };

  const onSelectField = (draftId, field) => (opt) => {
    const value = opt?.value || "";
    if (value === OTHER_VALUE && OTHER_META[field]) {
      openOther(draftId, field);
      return;
    }
    onUpdateDraft(draftId, (d) => ({ ...d, [field]: value }));
  };

  const renderCell = (draft, field) => {
    if (
      field.type === "dropdown" ||
      field.type === "temp"
    ) {
      const options = optionsForField(field.key);
      const selected =
        otherModal?.draftId === draft.id && otherModal?.field === field.key
          ? { value: OTHER_VALUE, label: "Other" }
          : findOption(options, draft[field.key] || "");
      return (
        <div className="epsf-imp-import-grid__dd">
          <Dropdown
            options={options}
            selected={selected}
            onSelect={onSelectField(draft.id, field.key)}
            size="sm"
            showSearch={field.type === "dropdown"}
            pinValues={field.type === "dropdown" ? ["Other"] : []}
            className="epsf-imp-import-dd"
          />
        </div>
      );
    }

    return (
      <input
        type="text"
        value={draft[field.key] ?? ""}
        placeholder={field.placeholder}
        title={
          field.key === "storageInstructions"
            ? draft[field.key] || field.placeholder
            : undefined
        }
        onChange={(e) =>
          onUpdateDraft(draft.id, (d) => ({
            ...d,
            [field.key]: e.target.value,
          }))
        }
      />
    );
  };

  return (
    <div className="epsf-imp-import-sheet">
      {showFailBanner ? (
        <div className="epsf-imp-import-sheet__banner epsf-imp-import-sheet__banner--warn">
          {statusMessage}
        </div>
      ) : null}

      {parseErrors.length > 0 ? (
        <div className="epsf-imp-import-sheet__banner epsf-imp-import-sheet__banner--warn">
          {parseErrors.slice(0, 6).map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
          {parseErrors.length > 6 ? (
            <div>…and {parseErrors.length - 6} more</div>
          ) : null}
        </div>
      ) : null}

      {drafts.length === 0 ? (
        <div className="epsf-imp-import-sheet__empty">
          <div className="epsf-imp-import-sheet__empty-icon" aria-hidden="true">
            <i className="fas fa-table" />
          </div>
          <h6>Add multiple IMP products</h6>
          <p>
            Download the template from the header, upload a filled Excel file,
            or add rows manually and edit them here before saving.
          </p>
          {typeof onAddRow === "function" ? (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm mt-2"
              onClick={onAddRow}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add row
            </button>
          ) : null}
        </div>
      ) : (
        <>
        <div className="epsf-imp-import-grid-wrap">
          <table className="epsf-imp-import-grid">
            <thead>
              <tr>
                <th className="epsf-imp-import-grid__col-num">#</th>
                {TEXT_FIELDS.map((field) => (
                  <th
                    key={field.key}
                    style={{ minWidth: field.minWidth }}
                    title={field.title}
                  >
                    {field.label}
                    {field.title ? (
                      <>
                        {" "}
                        <i
                          className="fas fa-info-circle text-muted"
                          aria-label={field.title}
                          title={field.title}
                          style={{ cursor: "help" }}
                        />
                      </>
                    ) : null}
                  </th>
                ))}
                <th className="epsf-imp-import-grid__col-actions" />
              </tr>
            </thead>
            <tbody>
              {sortedDrafts.map((draft, idx) => {
                const rowError = errorsById[draft.id];
                return (
                  <React.Fragment key={draft.id}>
                    <tr
                      className={
                        rowError ? "epsf-imp-import-grid__row--error" : ""
                      }
                    >
                      <td className="epsf-imp-import-grid__num">{idx + 1}</td>
                      {TEXT_FIELDS.map((field) => (
                        <td key={field.key}>{renderCell(draft, field)}</td>
                      ))}
                      <td className="epsf-imp-import-grid__actions">
                        <button
                          type="button"
                          className="epsf-imp-import-grid__remove"
                          onClick={() => onRemoveDraft(draft.id)}
                          title="Remove row"
                          aria-label={`Remove row ${idx + 1}`}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                    {rowError ? (
                      <tr className="epsf-imp-import-grid__error-row">
                        <td colSpan={TEXT_FIELDS.length + 2}>
                          <i
                            className="fas fa-circle-exclamation me-1"
                            aria-hidden="true"
                          />
                          {rowError}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {typeof onAddRow === "function" ? (
          <button
            type="button"
            className="epsf-imp-import-sheet__add-row"
            onClick={onAddRow}
          >
            <i className="fas fa-plus" aria-hidden="true" />
            Add row
          </button>
        ) : null}
        </>
      )}

      {otherModal ? (
        <div
          className="epsf-other-inline-overlay"
          onClick={closeOther}
          role="presentation"
        >
          <div
            className="epsf-other-inline-card"
            role="dialog"
            aria-modal="true"
            aria-label={otherModal.title}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="epsf-other-inline-card__head">
              <span className="epsf-other-inline-card__title">
                {otherModal.title}
              </span>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeOther}
              />
            </div>
            <div className="epsf-other-inline-card__body">
              <label className="form-label mb-1">
                {otherModal.label} <span className="mandatory">*</span>
              </label>
              <input
                className={`form-control form-control-sm${
                  otherModal.error ? " is-invalid" : ""
                }`}
                value={otherModal.draft}
                autoFocus
                maxLength={otherModal.maxLength}
                placeholder={otherModal.placeholder}
                onChange={(ev) =>
                  setOtherModal((prev) =>
                    prev
                      ? { ...prev, draft: ev.target.value, error: "" }
                      : prev,
                  )
                }
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") {
                    ev.preventDefault();
                    confirmOther();
                  }
                }}
              />
              {otherModal.error ? (
                <div className="epsf-form-field-error">{otherModal.error}</div>
              ) : null}
            </div>
            <div className="epsf-other-inline-card__foot">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={closeOther}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={confirmOther}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
